import React, { useState, useMemo, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { convertResponseDate, convertTimeToResponse, fieldTruePermission, formatPayloadDate } from "../../../utils/formHelper.js";
import ProFormsLayout from "../ProForms/ProFormsLayout/index.jsx";
import FormSectionWrapper from "../ProForms/FormSectionWrapper/index.jsx";
import GenericModal from "../Modals/GenericModal.jsx";
import ProEmailField from "../Inputs/EmailField.jsx";
import ProSelectField from "../Inputs/SelectField.jsx";
import ProMultiSelectSelectField from "../Inputs/MultiSelectField.jsx";
import ProDateField from "../Inputs/DateField.jsx";
import { successToast, errorToast } from "../../../utils/toastHelper.js";
import { ACTIVE_PROJECTS_DROPDOWN, GET_ALL_PARTNERS_DROPDOWN, REPORT_SCHEDULES, GET_ALL_PARTNERS_RAP_DROPDOWN, REPORT_SCHEDULER_JOBS } from "../../../utils/services/apiPath.js";
import { getApi, postApi, putApi } from "../../../utils/services/index.js";
import { fileTypeOptions, downloadFileTypeOptions, scheduleOptions, dailyFrequencyOptions } from "./constants";
import {
  mapFrequency,
  mapFrequencyToRunFields,
  parseScheduleFilterParams,
  resolveMultiSelectOptionsFromParams,
  syncSelectedMultiSelectOptions
} from "./helpers";
import dayjs from "dayjs";
import ProRadioGroup from "../Inputs/RadioGroup.jsx";
import { Box, Divider, LinearProgress, Typography } from "@mui/material";
import { jwtDecode } from "jwt-decode";
let utc = require("dayjs/plugin/utc.js")
dayjs.extend(utc)

const FilterSection = ({
  projectsList,
  fieldTruePermission,
  control,
  partnersList,
  filters,
  apprenticeList,
  enableMultiProjectPartnerFilters
}) => {
  const {
    apprenticeFilter,
    dateFilter,
    partnerFilter,
    projectFilter
  } = filters
  const noFilters = !apprenticeFilter && !dateFilter && !partnerFilter && !projectFilter
  return (
    <>
      {noFilters && <div>No filters are available for this report</div>}
      {projectFilter &&
        (enableMultiProjectPartnerFilters ? (
          <ProMultiSelectSelectField
            label="Project"
            options={projectsList}
            permission={fieldTruePermission}
            control={control}
            name="projectId"
            mode="edit"
            rules={{}}
            sx={{ width: "100%" }}
            fieldProps={{ disableCloseOnSelect: true }}
            hideSelected
            allOptionValue="All"
          />
        ) : (
          <ProSelectField
            label="Project"
            options={projectsList}
            permission={fieldTruePermission}
            control={control}
            name="projectId"
            defaultValue={""}
            sx={{ width: "100%" }}
            shouldSort={false}
          />
        ))}
      {partnerFilter &&
        (enableMultiProjectPartnerFilters ? (
          <ProMultiSelectSelectField
            label="Partner"
            options={partnersList}
            permission={fieldTruePermission}
            control={control}
            name="partnerId"
            mode="edit"
            rules={{}}
            sx={{ width: "100%" }}
            fieldProps={{ disableCloseOnSelect: true }}
            hideSelected
            allOptionValue="All"
          />
        ) : (
          <ProSelectField
            label="Partner"
            options={partnersList}
            permission={fieldTruePermission}
            control={control}
            name="partnerId"
            defaultValue={""}
            sx={{ width: "100%" }}
            shouldSort={false}
          />
        ))}
      {apprenticeFilter && <ProSelectField
        label="Approved Labor Classification Title"
        options={apprenticeList}
        permission={fieldTruePermission}
        control={control}
        name="apprenticeId"
        defaultValue={""}
        sx={{ width: "100%" }}
        shouldSort={false}
      />}
      {dateFilter &&
        <>
          <ProDateField
            label="Select Start Date"
            permission={fieldTruePermission}
            control={control}
            name="startDateFilter"
            mode="edit"
            sx={{ width: "100%" }}
          />
          <ProDateField
            label="Select End Date"
            permission={fieldTruePermission}
            control={control}
            name="endDateFilter"
            mode="edit"
            sx={{ width: "100%" }}
          />
        </>
      }
    </>
  )
}


const ScheduleReportModal = ({
  isModalOpen,
  setIsModalOpen,
  reportId,
  reportScheduleId = "",
  isEditMode = false,
  setIsDoneEditing,
  showScheduleSection = true,
  reportLink = "",
  enableMultiProjectPartnerFilters = false,
  downloadOnlyEnabled = true,
  filters = {},
  buttonText = "Save"
}) => {
  const [projectsList, setProjectsList] = useState([]);
  const [apprenticeList, setApprenticeList] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const [rapList, setRapList] = useState([]);
  const [exportState, setExportState] = useState({
    jobId: null,
    status: "",
    progressPercent: 0,
    message: "",
    rowsExported: null,
    downloadFileName: "",
    downloadContentType: "",
    downloadExpiresDate: null
  });
  const pollingRef = useRef(null);
  const pollingActiveRef = useRef(false);
  const pollingSessionRef = useRef(0);
  const handledCompletedJobsRef = useRef(new Set());
  const allowBackgroundPollingRef = useRef(false);
  const isMountedRef = useRef(true);
  const scheduleFilterParamsRef = useRef(null);
  const hasHydratedProjectsRef = useRef(false);
  const hasHydratedPartnersRef = useRef(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [partnersLoaded, setPartnersLoaded] = useState(false);
  const [scheduleFiltersLoaded, setScheduleFiltersLoaded] = useState(false);
  const { control, setValue, watch, handleSubmit, getValues, reset } = useForm({
    defaultValues: {
      emailList: "",
      runFrequency: scheduleOptions[0].value,
      dailyFrequency: "",
      starts: "",
      startDate: null,
      ends: "",
      endDate: null,
      fileType: "XLSX",
      projectId: enableMultiProjectPartnerFilters ? [] : null,
      partnerId: enableMultiProjectPartnerFilters ? [] : null,
      apprenticeId: null,
      startDateFilter: null,
      endDateFilter: null
    }
  });
  const project = watch("projectId");
  const partner = watch("partnerId");
  const selectedFileType = watch("fileType");

  const fetchReportSchedule = async () => {
    try {
      const data = await getApi(`${REPORT_SCHEDULES}/${reportScheduleId}`)
      scheduleFilterParamsRef.current = parseScheduleFilterParams(data?.reportParams);
      for (let k of Object.keys(getValues())) {
        if (k == "startDate" || k == "endDate") {
          continue;
        }
        if (k == "fileType") {
          setValue("fileType", data["reportFormat"])
        }
        if (data.hasOwnProperty(k)) {
          setValue(k, data[k])
        }
      }
      if (data?.runFrequency && data.runFrequency !== "daily") {
        setValue("dailyFrequency", mapFrequency(data));
      }
      if (data.startDate) setValue("startDate", convertResponseDate(data.startDate));
      if (data.endDate) setValue("endDate", convertResponseDate(data.endDate));
      if (scheduleFilterParamsRef.current) {
        const parsedFilters = scheduleFilterParamsRef.current;
        setValue("startDateFilter", parsedFilters.startDateFilter ? convertResponseDate(parsedFilters.startDateFilter) : null);
        setValue("endDateFilter", parsedFilters.endDateFilter ? convertResponseDate(parsedFilters.endDateFilter) : null);
        setValue("apprenticeId", parsedFilters.apprenticeId);
        if (!enableMultiProjectPartnerFilters) {
          setValue("projectId", parsedFilters.projectId);
          setValue("partnerId", parsedFilters.partnerId);
        }
      }
      setScheduleFiltersLoaded(true);
    } catch (e) {
      errorToast("Unable to fetch report schedule")
    }
  }
  useEffect(() => {
    if (!isModalOpen) {
      return
    }
    fetchProjects();
    if (!isEditMode) {
      return
    }
    fetchReportSchedule()
  }, [isModalOpen, isEditMode])

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    if (enableMultiProjectPartnerFilters) {
      fetchPartners();
      return;
    }
    if (project) {
      fetchPartners();
    }
  }, [isModalOpen, project, enableMultiProjectPartnerFilters]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    if (!enableMultiProjectPartnerFilters && project && partner) {
      fetchApprentice();
    }
  }, [isModalOpen, project, partner, enableMultiProjectPartnerFilters]);

  useEffect(() => {
    if (isModalOpen) {
      return;
    }
    scheduleFilterParamsRef.current = null;
    hasHydratedProjectsRef.current = false;
    hasHydratedPartnersRef.current = false;
    setProjectsLoaded(false);
    setPartnersLoaded(false);
    setScheduleFiltersLoaded(false);
  }, [isModalOpen]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (!allowBackgroundPollingRef.current) {
        stopPolling();
      }
    };
  }, []);

  const getSelectedProjectGuid = () => {
    if (!enableMultiProjectPartnerFilters) {
      return project || "All";
    }
    if (!Array.isArray(project) || project.length !== 1) {
      return "All";
    }
    return project[0]?.value || "All";
  };

  const fetchProjects = async () => {
    setProjectsLoaded(false);
    try {
      const response = await getApi(ACTIVE_PROJECTS_DROPDOWN);
      const projects = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      if (!projects.length) {
        setProjectsList([]);
        setValue("projectId", null);
        if (response?.error) {
          throw new Error(response.error);
        }
        throw new Error("No projects available.");
      }

      const list = projects.map(item => ({ label: item.projectName, value: item.projectId }));
      const existingAll = list.find(
        item =>
          item?.value === "All" ||
          (typeof item?.label === "string" && item.label.trim().toLowerCase() === "all projects")
      );
      if (!existingAll) {
        list.unshift({ label: "All Projects", value: "All" });
      }
      if (!enableMultiProjectPartnerFilters) {
        setValue("projectId", existingAll?.value ?? "All");
      } else {
        setValue("projectId", []);
      }
      setProjectsList(list);
    } catch (error) {
      setProjectsList([]);
      setValue("projectId", null);
      errorToast(error?.message || "Unable to fetch projects");
    } finally {
      setProjectsLoaded(true);
    }
  };

  const fetchPartners = async () => {
    setPartnersLoaded(false);
    try {
      const response = await getApi(`${GET_ALL_PARTNERS_DROPDOWN}?projectId=${getSelectedProjectGuid()}`);
      const partnerData = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      if (!partnerData.length) {
        if (enableMultiProjectPartnerFilters) {
          setPartnersList([]);
          if (!isEditMode || hasHydratedPartnersRef.current) {
            setValue("partnerId", []);
          }
        } else {
          setPartnersList([{ label: "All Partners", value: "All" }]);
          setValue("partnerId", "All");
        }
        if (response?.error) {
          throw new Error(response.error);
        }
        return;
      }

      const list = partnerData.map(item => ({ label: item.companyName, value: item.clientGuid }));
      const existingAll = list.find(
        item =>
          item?.value === "All" ||
          (typeof item?.label === "string" && item.label.trim().toLowerCase() === "all partners")
      );
      if (!existingAll) {
        list.unshift({ label: "All Partners", value: "All" });
      }
      if (!enableMultiProjectPartnerFilters) {
        setValue("partnerId", "All");
      } else {
        const shouldPreserveHydration = isEditMode && !hasHydratedPartnersRef.current && !!scheduleFilterParamsRef.current;
        if (!shouldPreserveHydration) {
          setValue("partnerId", syncSelectedMultiSelectOptions({
            currentValue: getValues("partnerId"),
            options: list
          }));
        }
      }
      setPartnersList(list);
    } catch (error) {
      setPartnersList([]);
      setValue("partnerId", null);
      errorToast(error?.message || "Unable to fetch partners");
    } finally {
      setPartnersLoaded(true);
    }
  };

  const fetchApprentice = async () => {
    try {
      const response = await getApi(`${GET_ALL_PARTNERS_RAP_DROPDOWN}?projectGuid=${project}&partnerGuid=${partner}`);
      const apprenticeData = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      const list = apprenticeData.map(item => ({ label: item.label, value: item.value }));
      setApprenticeList(list);
    } catch (err) {
      setApprenticeList([]);
    }
  };

  const [isSaving, setIsSaving] = useState(false);
  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
  const runFrequency = watch("runFrequency");

  useEffect(() => {
    if (!isModalOpen || !isEditMode || !enableMultiProjectPartnerFilters || hasHydratedProjectsRef.current || !projectsLoaded || !scheduleFiltersLoaded) {
      return;
    }

    const parsedFilters = scheduleFilterParamsRef.current;
    if (!parsedFilters) {
      return;
    }

    setValue("projectId", resolveMultiSelectOptionsFromParams({
      options: projectsList,
      singleValue: parsedFilters.projectId,
      csvValues: parsedFilters.projectIds
    }));
    hasHydratedProjectsRef.current = true;
    hasHydratedPartnersRef.current = false;
  }, [isModalOpen, isEditMode, enableMultiProjectPartnerFilters, projectsLoaded, projectsList, setValue, scheduleFiltersLoaded]);

  useEffect(() => {
    if (!isModalOpen || !isEditMode || !enableMultiProjectPartnerFilters || hasHydratedPartnersRef.current || !partnersLoaded || !scheduleFiltersLoaded) {
      return;
    }

    const parsedFilters = scheduleFilterParamsRef.current;
    if (!parsedFilters) {
      return;
    }

    if (filters.projectFilter) {
      const expectedProjectValues = parsedFilters.projectIds.length > 0
        ? parsedFilters.projectIds
        : parsedFilters.projectId
          ? [parsedFilters.projectId]
          : [];
      const currentProjectValues = Array.isArray(project)
        ? project.map(item => item?.value).filter(Boolean)
        : [];
      const normalizedExpectedProjects = [...expectedProjectValues].sort();
      const normalizedCurrentProjects = [...currentProjectValues].sort();

      if (
        normalizedExpectedProjects.length !== normalizedCurrentProjects.length ||
        normalizedExpectedProjects.some((value, index) => value !== normalizedCurrentProjects[index])
      ) {
        return;
      }
    }

    setValue("partnerId", resolveMultiSelectOptionsFromParams({
      options: partnersList,
      singleValue: parsedFilters.partnerId,
      csvValues: parsedFilters.partnerIds
    }));
    hasHydratedPartnersRef.current = true;
  }, [isModalOpen, isEditMode, enableMultiProjectPartnerFilters, filters.projectFilter, partnersLoaded, partnersList, project, setValue, scheduleFiltersLoaded]);

  const pickFirstDefined = (...values) => values.find(value => value !== undefined && value !== null);
  const getCaseInsensitiveValue = (source, keys) => {
    if (!source || typeof source !== "object") {
      return undefined;
    }
    const sourceEntries = Object.entries(source);
    for (const key of keys) {
      const directValue = source[key];
      if (directValue !== undefined && directValue !== null) {
        return directValue;
      }
      const match = sourceEntries.find(([entryKey]) => entryKey.toLowerCase() === key.toLowerCase());
      if (match && match[1] !== undefined && match[1] !== null) {
        return match[1];
      }
    }
    return undefined;
  };
  const collectResponseCandidates = raw => {
    const queue = [raw];
    const candidates = [];
    const visited = new Set();
    while (queue.length > 0 && candidates.length < 12) {
      const current = queue.shift();
      if (!current || typeof current !== "object") {
        continue;
      }
      if (visited.has(current)) {
        continue;
      }
      visited.add(current);
      candidates.push(current);
      ["data", "result", "payload", "value", "item"].forEach(key => {
        const nested = getCaseInsensitiveValue(current, [key]);
        if (nested && typeof nested === "object") {
          queue.push(nested);
        }
      });
    }
    return candidates;
  };
  // Scheduler responses vary slightly by environment/proxy shape.
  // Convert once so polling and UI state use a stable contract.
  const mapSchedulerJobResponse = (raw, fallbackJobId = null) => {
    if (!raw || raw.error) {
      return null;
    }

    const candidates = collectResponseCandidates(raw);
    const candidate = candidates.find(item => {
      const jobIdCandidate = getCaseInsensitiveValue(item, ["jobId", "reportExportJobGuid", "id"]);
      const statusCandidate = getCaseInsensitiveValue(item, ["status", "jobStatus"]);
      const progressCandidate = getCaseInsensitiveValue(item, ["progressPercent", "progress"]);
      const messageCandidate = getCaseInsensitiveValue(item, ["message"]);
      return (
        jobIdCandidate !== undefined ||
        statusCandidate !== undefined ||
        progressCandidate !== undefined ||
        messageCandidate !== undefined
      );
    });

    const source = candidate || raw;
    const normalizedJobId = pickFirstDefined(
      getCaseInsensitiveValue(source, ["jobId", "reportExportJobGuid", "id"]),
      fallbackJobId
    );
    const normalizedStatus = normalizeJobStatus(getCaseInsensitiveValue(source, ["status", "jobStatus"])) || "QUEUED";
    const normalizedProgress = Number(getCaseInsensitiveValue(source, ["progressPercent", "progress"]));

    return {
      jobId: normalizedJobId || null,
      status: normalizedStatus,
      progressPercent: Number.isFinite(normalizedProgress) ? normalizedProgress : 0,
      message: getCaseInsensitiveValue(source, ["message"]) || "",
      downloadUrl: getCaseInsensitiveValue(source, ["downloadUrl"]) || "",
      rowsExported: pickFirstDefined(getCaseInsensitiveValue(source, ["rowsExported"]), null),
      downloadFileName: getCaseInsensitiveValue(source, ["downloadFileName"]) || "",
      downloadContentType: getCaseInsensitiveValue(source, ["downloadContentType"]) || "",
      downloadExpiresDate: pickFirstDefined(getCaseInsensitiveValue(source, ["downloadExpiresDate"]), null),
      outputFormat: pickFirstDefined(getCaseInsensitiveValue(source, ["outputFormat", "reportFormat"]), null)
    };
  };
  const normalizeJobStatus = status => (typeof status === "string" ? status.trim().toUpperCase() : "");
  const canonicalizeJobStatus = status => normalizeJobStatus(status).replace(/[^A-Z]/g, "");
  const isTerminalStatus = status => {
    const canonicalStatus = canonicalizeJobStatus(status);
    return (
      canonicalStatus.startsWith("COMPLETED") ||
      canonicalStatus.startsWith("FAILED") ||
      canonicalStatus.startsWith("EXPIRED") ||
      canonicalStatus.startsWith("CANCELLED") ||
      canonicalStatus.startsWith("READYTODOWNLOAD")
    );
  };
  const isTerminalExportState = state => {
    if (!state?.jobId) {
      return false;
    }
    if (isTerminalStatus(state.status)) {
      return true;
    }
    const progress = Number(state.progressPercent);
    const normalizedMessage = normalizeJobStatus(state.message || "");
    if (Number.isFinite(progress) && progress >= 100) {
      return true;
    }
    return normalizedMessage.includes("READY TO DOWNLOAD");
  };
  const currentExportStatus = exportState.status;
  const hasActiveExportJob = isDownloadInProgress;
  const disableDownloadButton = isSaving || isDownloadInProgress;
  const disableScheduleButton =
    !project || !partner;

  useEffect(() => {
    if (!isModalOpen) {
      setIsSaving(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    // In download-only mode keep a valid default format selected so
    // the action button does not remain disabled after terminal states.
    if (isModalOpen && !showScheduleSection && downloadOnlyEnabled && !selectedFileType) {
      setValue("fileType", "XLSX");
    }
  }, [isModalOpen, showScheduleSection, downloadOnlyEnabled, selectedFileType, setValue]);

  useEffect(() => {
    if (isTerminalStatus(currentExportStatus)) {
      setIsSaving(false);
    }
  }, [currentExportStatus]);

  useEffect(() => {
    if (!hasActiveExportJob && isSaving) {
      setIsSaving(false);
    }
  }, [hasActiveExportJob, isSaving]);

  useEffect(() => {
    if (!isDownloadInProgress || isSaving) {
      return;
    }
    if (pollingActiveRef.current) {
      return;
    }
    if (!exportState.jobId || isTerminalExportState(exportState) || handledCompletedJobsRef.current.has(exportState.jobId)) {
      setIsDownloadInProgress(false);
    }
  }, [isDownloadInProgress, isSaving, exportState.jobId, exportState.status, exportState.progressPercent, exportState.message]);

  const dailyFrequencyLabel = useMemo(() => {
    return (
      {
        weekly: "Weekly Frequency",
        monthly: "Monthly Frequency",
        yearly: "Yearly Frequency",
        daily: "Daily Frequency"
      }[runFrequency] || "Daily Frequency"
    );
  }, [runFrequency]);

  const dailyFrequencyPlaceholder = useMemo(() => {
    return (
      {
        weekly: "Select a weekly frequency",
        monthly: "Select a monthly frequency",
        yearly: "Select a yearly frequency",
        daily: "Select a daily frequency"
      }[runFrequency] || "Select a daily frequency"
    );
  }, [runFrequency]);

  const handleCancel = () => {
    const hasActiveExport = downloadOnlyEnabled && hasActiveExportJob;

    if (!hasActiveExport) {
      if (pollingRef.current) {
        window.clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      allowBackgroundPollingRef.current = false;
    } else {
      // Keep polling after modal close so download still triggers in background.
      allowBackgroundPollingRef.current = true;
    }

    // Avoid leaving action button in loading state after modal closes.
    setIsSaving(false);
    setIsModalOpen(false);
    if (!hasActiveExport) {
      setIsDownloadInProgress(false);
      resetFormFields();
      setExportState({
        jobId: null,
        status: "",
        progressPercent: 0,
        message: "",
        rowsExported: null,
        downloadFileName: "",
        downloadContentType: "",
        downloadExpiresDate: null
      });
    }
  }

  const getFilterText = ({ startDateFilter, endDateFilter, projectId, partnerId, apprenticeId }) => {
    const selectedProjectIds = enableMultiProjectPartnerFilters && Array.isArray(projectId)
      ? projectId.map(item => item?.value).filter(Boolean)
      : [];
    const selectedPartnerIds = enableMultiProjectPartnerFilters && Array.isArray(partnerId)
      ? partnerId.map(item => item?.value).filter(Boolean)
      : [];
    const selectedProject = enableMultiProjectPartnerFilters
      ? (selectedProjectIds.length === 1 ? selectedProjectIds[0] : "All")
      : (projectId || "All");
    const selectedPartner = enableMultiProjectPartnerFilters
      ? (selectedPartnerIds.length === 1 ? selectedPartnerIds[0] : "All")
      : (partnerId || "All");
    let filter = `projectGuid=${selectedProject}&partnerGuid=${selectedPartner}`
    if (enableMultiProjectPartnerFilters && selectedProjectIds.length > 1) {
      filter += `&projectGuidsCsv=${selectedProjectIds.join(",")}`;
    }
    if (enableMultiProjectPartnerFilters && selectedPartnerIds.length > 1) {
      filter += `&partnerGuidsCsv=${selectedPartnerIds.join(",")}`;
    }
    if (apprenticeId) {
      filter += `&apprenticeGuid=${apprenticeId}`;
    }
    if (startDateFilter) {
      filter += `&dateFrom=${formatPayloadDate(startDateFilter)}`
    }
    if (endDateFilter) {
      filter += `&dateTo=${formatPayloadDate(endDateFilter)}`
    }
    return `?${filter}`;
  }

  const resetFormFields = () => {
    reset({
      emailList: "",
      runFrequency: scheduleOptions[0].value,
      dailyFrequency: "",
      starts: "",
      startDate: null,
      ends: "",
      endDate: null,
      fileType: "XLSX",
      projectId: enableMultiProjectPartnerFilters ? [] : "All",
      partnerId: enableMultiProjectPartnerFilters ? [] : "All",
      apprenticeId: null,
      startDateFilter: null,
      endDateFilter: null
    });
  }

  function stopPolling() {
    pollingActiveRef.current = false;
    pollingSessionRef.current += 1;
    if (pollingRef.current) {
      window.clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }

  const clearExportState = () => {
    setExportState({
      jobId: null,
      status: "",
      progressPercent: 0,
      message: "",
      rowsExported: null,
      downloadFileName: "",
      downloadContentType: "",
      downloadExpiresDate: null
    });
    setIsDownloadInProgress(false);
  };

  // Resolve client/user ids from both cached profile and token claims
  // to avoid enqueuing jobs with missing tenant context.
  const getCurrentUserContext = () => {
    let localUser = {};
    try {
      localUser = JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      localUser = {};
    }

    let tokenPayload = {};
    try {
      const token = localStorage.getItem("accessToken");
      tokenPayload = token ? jwtDecode(token) : {};
    } catch {
      tokenPayload = {};
    }

    const resolvePositiveNumber = (...values) => {
      for (const value of values) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
          return parsed;
        }
      }
      return null;
    };

    return {
      globalClientId: resolvePositiveNumber(
        localUser?.globalClientId,
        tokenPayload?.globalClientId,
        tokenPayload?.GlobalClientId,
        tokenPayload?.GlobalClientID,
        localUser?.GlobalClientId,
        tokenPayload?.globalClientID,
        localUser?.globalClientID,
        tokenPayload?.clientId,
        localUser?.clientId
      ),
      requestedByClientUserId: resolvePositiveNumber(
        tokenPayload?.clientUserId,
        tokenPayload?.ClientUserId,
        tokenPayload?.userId,
        localUser?.clientUserId,
        localUser?.ClientUserId,
        localUser?.userId
      ) || 0
    };
  };

  const getDownloadExpiryLabel = value => {
    if (!value) {
      return "";
    }
    const dateValue = dayjs(value);
    if (!dateValue.isValid()) {
      return "";
    }
    return `Available until ${dateValue.local().format("MMM D, YYYY h:mm A")}`;
  };

  const downloadExportedJob = async (downloadUrl, fileName = "") => {
    if (!downloadUrl) {
      throw new Error("Download URL is not available.");
    }

    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    if (fileName) {
      anchor.download = fileName;
    }
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const getErrorMessage = (error, fallbackMessage = "Unknown error") => {
    if (!error) {
      return fallbackMessage;
    }
    if (typeof error === "string") {
      return error;
    }
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.message) {
      return error.message;
    }
    return fallbackMessage;
  };

  const startPollingJob = (jobId) => {
    stopPolling();
    pollingActiveRef.current = true;
    const sessionId = pollingSessionRef.current;

    const poll = async () => {
      try {
        const rawResponse = await getApi(`${REPORT_SCHEDULER_JOBS}/${jobId}`);
        if (!pollingActiveRef.current || pollingSessionRef.current !== sessionId) {
          return;
        }

        if (rawResponse?.error) {
          stopPolling();
          if (isMountedRef.current) {
            setIsSaving(false);
            setIsDownloadInProgress(false);
            setExportState({
              jobId,
              status: "Failed",
              progressPercent: 100,
              message: "Unable to get export status"
            });
          }
          errorToast("Unable to get export status.");
          return;
        }

        const response = mapSchedulerJobResponse(rawResponse, jobId);
        if (!response?.jobId) {
          stopPolling();
          if (isMountedRef.current) {
            setIsSaving(false);
            setIsDownloadInProgress(false);
            setExportState({
              jobId,
              status: "Failed",
              progressPercent: 100,
              message: "Unexpected export status payload."
            });
          }
          errorToast("Unable to parse export status response.");
          return;
        }

        if (isMountedRef.current) {
          setExportState({
            jobId: response.jobId,
            status: response.status,
            progressPercent: response.progressPercent,
            message: response.message,
            rowsExported: response.rowsExported,
            downloadFileName: response.downloadFileName,
            downloadContentType: response.downloadContentType,
            downloadExpiresDate: response.downloadExpiresDate
          });
        }

        const canonicalStatus = canonicalizeJobStatus(response.status);
        const normalizedMessage = normalizeJobStatus(response.message || "");
        const responseProgress = Number(response.progressPercent);
        const isCompletedLikeStatus =
          canonicalStatus.startsWith("COMPLETED") ||
          canonicalStatus.startsWith("READYTODOWNLOAD") ||
          (Number.isFinite(responseProgress) && responseProgress >= 100) ||
          normalizedMessage.includes("READY TO DOWNLOAD");
        if (isCompletedLikeStatus) {
          stopPolling();
          allowBackgroundPollingRef.current = false;
          if (isMountedRef.current) {
            setIsSaving(false);
            setIsDownloadInProgress(false);
          }
          if (handledCompletedJobsRef.current.has(jobId)) {
            if (isMountedRef.current) {
              clearExportState();
            }
            return;
          }
          handledCompletedJobsRef.current.add(jobId);
          try {
            await downloadExportedJob(response.downloadUrl, response.downloadFileName);
            const exportedRows = Number.isFinite(response.rowsExported) ? response.rowsExported : null;
            successToast(
              exportedRows !== null
                ? `Report export ready. Download started (${exportedRows} rows).`
                : "Report export ready. Download started."
            );
            if (isMountedRef.current) {
              clearExportState();
            }
          } catch (downloadError) {
            const errorMessage = getErrorMessage(downloadError, "Unable to export real report data.");
            handledCompletedJobsRef.current.delete(jobId);
            if (isMountedRef.current) {
              setExportState(prev => ({ ...prev, status: "Failed", progressPercent: 100, message: errorMessage }));
              setIsSaving(false);
            }
            errorToast(errorMessage);
          }
          return;
        }

        if (
          canonicalStatus.startsWith("FAILED") ||
          canonicalStatus.startsWith("EXPIRED") ||
          canonicalStatus.startsWith("CANCELLED")
        ) {
          stopPolling();
          allowBackgroundPollingRef.current = false;
          if (isMountedRef.current) {
            setIsSaving(false);
            setIsDownloadInProgress(false);
          }
          errorToast(response.message || "Report export failed.");
          if (isMountedRef.current) {
            setExportState(prev => ({ ...prev, status: "Failed", progressPercent: 100 }));
          }
          return;
        }

        // Schedule next poll only after current request is completed.
        if (pollingActiveRef.current) {
          pollingRef.current = window.setTimeout(poll, 2500);
        }
      } catch (error) {
        if (!pollingActiveRef.current || pollingSessionRef.current !== sessionId) {
          return;
        }
        stopPolling();
        allowBackgroundPollingRef.current = false;
        if (isMountedRef.current) {
          setIsSaving(false);
          setIsDownloadInProgress(false);
        }
        const errorMessage = getErrorMessage(error, "Unable to get export status");
        if (isMountedRef.current) {
          setExportState({
            jobId,
            status: "Failed",
            progressPercent: 100,
            message: errorMessage
          });
        }
        errorToast(errorMessage);
      }
    };

    pollingRef.current = window.setTimeout(poll, 0);
  };

  useEffect(() => {
    if (!isModalOpen || !downloadOnlyEnabled) {
      return;
    }
    if (!isDownloadInProgress || !exportState.jobId || isTerminalStatus(exportState.status)) {
      return;
    }
    if (!pollingActiveRef.current) {
      startPollingJob(exportState.jobId);
    }
  }, [isModalOpen, downloadOnlyEnabled, isDownloadInProgress, exportState.jobId, exportState.status, selectedFileType]);

  const handleViewReport = async (formData) => {
    if (!downloadOnlyEnabled) {
      const filterText = getFilterText(formData);
      setIsModalOpen(false);
      window.open(`${reportLink}${filterText}`, "_blank");
      return;
    }

    if (hasActiveExportJob) {
      return;
    }

    setIsSaving(true);
    setIsDownloadInProgress(true);
    const reportParams = getFilterText(formData);
    const userContext = getCurrentUserContext();
    if (!userContext.globalClientId) {
      setIsSaving(false);
      setIsDownloadInProgress(false);
      errorToast("Unable to identify client context for report download.");
      return;
    }
    const payload = {
      reportId: Number(reportId),
      globalClientId: userContext.globalClientId,
      requestedByClientUserId: userContext.requestedByClientUserId,
      outputFormat: formData.fileType || "XLSX",
      reportParams
    };

    try {
      const createdJobRaw = await postApi(REPORT_SCHEDULER_JOBS, payload);
      const createdJob = mapSchedulerJobResponse(createdJobRaw);
      if (createdJobRaw?.error || !createdJob?.jobId) {
        setIsSaving(false);
        setIsDownloadInProgress(false);
        errorToast(createdJobRaw?.error || "Failed to queue report export. Please try again.");
        return;
      }
      handledCompletedJobsRef.current.delete(createdJob.jobId);

      setExportState({
        jobId: createdJob.jobId,
        status: normalizeJobStatus(createdJob.status) || "QUEUED",
        progressPercent: createdJob.progressPercent || 0,
        message: createdJob.message || "Queued",
        rowsExported: createdJob.rowsExported ?? null,
        downloadFileName: createdJob.downloadFileName || "",
        downloadContentType: createdJob.downloadContentType || "",
        downloadExpiresDate: createdJob.downloadExpiresDate || null
      });
      allowBackgroundPollingRef.current = true;
      startPollingJob(createdJob.jobId);
      resetFormFields();
    } catch (error) {
      setIsSaving(false);
      setIsDownloadInProgress(false);
      errorToast(getErrorMessage(error, "Failed to queue report export. Please try again."));
    }
  }

  const handleSave = async formData => {
    setIsSaving(true);
    const runFields = mapFrequencyToRunFields(formData.dailyFrequency);

    const midnightUTC = new Date();
    midnightUTC.setTime(0, 0, 0, 0);
    const reportParams = getFilterText(formData)

    const payload = {
      ...runFields,
      reportId: reportId,
      reportFormat: formData.fileType,
      compressFile: true,
      reportParams: "string",
      emailList: formData.emailList || "",
      runFrequency: formData.runFrequency || "Daily",
      runTime: runFrequency == "daily" ? dayjs.utc().format("HH:mm:ss") : convertTimeToResponse(midnightUTC),
      startDate: formatPayloadDate(formData.startDate) || null,
      endDate: formatPayloadDate(formData.endDate) || null,
      isActive: true,
      reportParams: reportParams
    };

    if (isEditMode) {
      payload["reportScheduleId"] = reportScheduleId
    }

    try {
      const method = isEditMode ? putApi : postApi
      const url = isEditMode ? `${REPORT_SCHEDULES}/${reportScheduleId}` : REPORT_SCHEDULES
      const { error } = await method(url, payload);
      if (error) {
        throw new Error(error);
      }
      successToast("Schedule saved successfully!");
      setIsModalOpen(false);
      resetFormFields();
      if (isEditMode) {
        setIsDoneEditing(true)
      }
    } catch (err) {
      errorToast(err.message || "Failed to save the schedule. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <GenericModal
      open={isModalOpen}
      setOpen={setIsModalOpen}
      onCloseAction={handleCancel}
      modalTitle={showScheduleSection ? "Schedule Reports" : "View Report Filters"}
    >
      <ProFormsLayout
        disableSaveBtn={
          showScheduleSection
            ? disableScheduleButton
            : downloadOnlyEnabled
              ? disableDownloadButton
              : disableScheduleButton
        }
        title={showScheduleSection ? "Schedule Details" : "Report Filters"}
        beingEdited={true}
        onCancel={handleCancel}
        onSave={showScheduleSection ? handleSubmit(handleSave) : handleSubmit(handleViewReport)}
        hideEdit={true}
        isSaving={isSaving}
        buttonText={showScheduleSection ? buttonText : (downloadOnlyEnabled ? "Download" : buttonText)}
      >
        <div className="proFormSample-content">
          {!showScheduleSection && downloadOnlyEnabled && exportState.jobId && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{exportState.status || "Queued"}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {exportState.message || "Preparing download..."}
              </Typography>
              {!!exportState.downloadFileName && (
                <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  File: {exportState.downloadFileName}
                </Typography>
              )}
              {Number.isFinite(exportState.rowsExported) && (
                <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Rows exported: {exportState.rowsExported}
                </Typography>
              )}
              {!!exportState.downloadExpiresDate && (
                <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                  {getDownloadExpiryLabel(exportState.downloadExpiresDate)}
                </Typography>
              )}
              <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, exportState.progressPercent || 0))} />
            </Box>
          )}
          {showScheduleSection &&
            <>
              <FormSectionWrapper>
                <ProEmailField
                  label="Email Address"
                  name="emailList"
                  mode="edit"
                  rules={{ required: "Enter your email address" }}
                  permission={fieldTruePermission}
                  control={control}
                  sx={{ width: "100%" }}
                />

                <ProSelectField
                  label="Select Frequency"
                  options={scheduleOptions}
                  permission={fieldTruePermission}
                  control={control}
                  name="runFrequency"
                  defaultValue={scheduleOptions[0].value}
                  sx={{ width: "100%" }}
                  shouldSort={false}
                />

                <ProSelectField
                  label={dailyFrequencyLabel}
                  placeholder={dailyFrequencyPlaceholder}
                  options={
                    runFrequency === "weekly"
                      ? dailyFrequencyOptions.weekly
                      : runFrequency === "monthly"
                        ? dailyFrequencyOptions.monthly
                        : runFrequency === "yearly"
                          ? dailyFrequencyOptions.yearly
                          : []
                  }
                  permission={fieldTruePermission}
                  control={control}
                  name="dailyFrequency"
                  defaultValue=""
                  disabled={runFrequency === "daily"}
                  sx={{ width: "100%" }}
                  shouldSort={false}
                />

                <ProDateField
                  label="Select Start Date"
                  permission={fieldTruePermission}
                  control={control}
                  name="startDate"
                  mode="edit"
                  sx={{ width: "100%" }}
                />
                <ProDateField
                  label="Select End Date"
                  permission={fieldTruePermission}
                  control={control}
                  name="endDate"
                  mode="edit"
                  sx={{ width: "100%" }}
                />

                <ProRadioGroup
                  label="File Type"
                  options={fileTypeOptions}
                  permission={fieldTruePermission}
                  control={control}
                  name="fileType"
                  sx={{ width: "100%" }}
                  rules={{ required: "File type is required" }}
                />
              </FormSectionWrapper>
              <Divider style={{ paddingBottom: "15px" }}>
                Filters
              </Divider>
            </>
          }

          <FormSectionWrapper>
            {!showScheduleSection && downloadOnlyEnabled &&
              <ProRadioGroup
                label="Download Format"
                options={downloadFileTypeOptions}
                mode="edit"
                permission={fieldTruePermission}
                control={control}
                name="fileType"
                sx={{ width: "100%" }}
                rules={{ required: "File type is required" }}
              />
            }
            <FilterSection
              filters={filters}
              fieldTruePermission={fieldTruePermission}
              control={control}
              projectsList={projectsList}
              apprenticeList={apprenticeList}
              partnersList={partnersList}
              enableMultiProjectPartnerFilters={enableMultiProjectPartnerFilters}
            />
          </FormSectionWrapper>

        </div>
      </ProFormsLayout>
    </GenericModal>
  );
};

export default ScheduleReportModal;
